import { useState } from 'preact/hooks'
import { InitData, tenantSiteUrl, assetUrl } from '../../core'
import { t } from '../../core/i18n'
import { GiftCardModal } from '../../components/GiftCardModal'

interface Props {
  initData: InitData
  ctaText?: string | null
  ctaLink?: string | null
}

/**
 * GiftCardsWidget — mirrors HS-native gift card promo styling.
 *
 * Layout:
 *   - Card with icon side + content side (stacks on mobile)
 *   - Title (large, bold)
 *   - Subtitle (muted)
 *   - Bullet list with orange ✓ checkmarks
 *   - "Køb gavekort" filled orange CTA → /gavekort on tenant subdomain
 *
 * Renders nothing if giftCard is absent — no empty state.
 */
export function GiftCardsWidget({ initData, ctaText = null, ctaLink = null }: Props) {
  const giftCard = initData.giftCard
  // Åbner inline-købs-modalen på studiets egen side (handoff beholdes som fallback).
  const [open, setOpen] = useState(false)

  if (!giftCard) {
    return null
  }

  const { title, subtitle, bullets } = giftCard
  const hasContent =
    (title && title.trim().length > 0) ||
    (subtitle && subtitle.trim().length > 0) ||
    (bullets && bullets.length > 0)

  if (!hasContent) {
    return null
  }

  const giftHref = ctaLink || `${tenantSiteUrl(initData)}/gavekort`
  const str = t(initData.locale)
  const ctaLabel = ctaText || str.buyGiftCard

  return (
    <div>
      <style>{giftCardStyles}</style>
      <div class="hsw-gift-wrap">
        <div class="hsw-gift-card">
          {/* Studiets eget billede (OPG-0064) med alt-tekst, ellers 🎁 (pynt, skjult for skærmlæsere). */}
          {giftCard.imageUrl ? (
            <div class="hsw-gift-media hsw-gift-media--image">
              <img class="hsw-gift-image" src={assetUrl(giftCard.imageUrl)} alt={title?.trim() || str.giftCardImageAlt} loading="lazy" />
            </div>
          ) : (
            <div class="hsw-gift-media" aria-hidden="true">
              <div class="hsw-gift-icon">🎁</div>
            </div>
          )}
          <div class="hsw-gift-content">
            {title && <h3 class="hsw-gift-title">{title}</h3>}
            {subtitle && <p class="hsw-gift-subtitle">{subtitle}</p>}
            {bullets && bullets.length > 0 && (
              <ul class="hsw-gift-features">
                {bullets.map((bullet, i) => (
                  <li key={i}>
                    <span class="hsw-check">✓</span>
                    {bullet}
                  </li>
                ))}
              </ul>
            )}
            <a
              href={giftHref}
              class="hsw-gift-cta"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: Event) => { e.preventDefault(); setOpen(true) }}
            >
              {ctaLabel}
            </a>
          </div>
        </div>
      </div>
      {open && (
        <GiftCardModal initData={initData} giftHref={giftHref} onClose={() => setOpen(false)} />
      )}
    </div>
  )
}

const giftCardStyles = `
  .hsw-gift-wrap {
    max-width: 1100px;
    margin: 0 auto;
  }
  .hsw-gift-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 0;
    display: grid;
    grid-template-columns: minmax(220px, 1fr) 2fr;
    gap: 0;
    overflow: hidden;
    transition: all .3s;
  }
  .hsw-gift-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }
  .hsw-gift-media {
    background: linear-gradient(135deg, var(--yoga-primary, #FF6B35), var(--yoga-secondary, var(--yoga-primary-900, #1A1A2E)));
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    padding: 2rem;
  }
  /* Studiets eget billede fylder hele billedfeltet (OPG-0064). */
  .hsw-gift-media--image {
    padding: 0;
    background: var(--yoga-primary-50, #f5f5f5);
  }
  .hsw-gift-image {
    display: block;
    width: 100%;
    height: 100%;
    min-height: inherit;
    object-fit: cover;
  }
  .hsw-gift-icon {
    font-size: 5rem;
    line-height: 1;
    filter: drop-shadow(0 4px 14px rgba(0,0,0,0.2));
  }
  .hsw-gift-content {
    padding: 2.5rem 2rem;
    display: flex;
    flex-direction: column;
  }
  .hsw-gift-title {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
    line-height: 1.2;
  }
  .hsw-gift-subtitle {
    font-size: 1rem;
    color: var(--yoga-text-muted, #525252);
    margin: 0 0 1.25rem;
    line-height: 1.5;
  }
  .hsw-gift-features {
    list-style: none;
    padding: 0;
    margin: 0 0 1.75rem;
  }
  .hsw-gift-features li {
    padding: 0.55rem 0;
    padding-left: 1.75rem;
    position: relative;
    font-size: 0.95rem;
    color: var(--yoga-text, #404040);
    line-height: 1.4;
  }
  .hsw-check {
    position: absolute;
    left: 0;
    color: var(--yoga-primary, #FF6B35);
    font-weight: 700;
    font-size: 1rem;
  }
  .hsw-gift-cta {
    display: inline-block;
    align-self: flex-start;
    margin-top: auto;
    padding: 0.9rem 1.75rem;
    text-align: center;
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    font-weight: 600;
    border: 1px solid var(--yoga-primary, #FF6B35);
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-size: 0.95rem;
    letter-spacing: 0.01em;
    transition: all .3s;
  }
  .hsw-gift-cta:hover {
    background: var(--yoga-primary-900, #1A1A2E);
    border-color: var(--yoga-primary-900, #1A1A2E);
    color: #fff;
  }
  @media (max-width: 640px) {
    .hsw-gift-card {
      grid-template-columns: 1fr;
    }
    .hsw-gift-media {
      min-height: 140px;
    }
    .hsw-gift-icon {
      font-size: 3.5rem;
    }
    .hsw-gift-content {
      padding: 1.75rem 1.5rem;
    }
    .hsw-gift-title {
      font-size: 1.5rem;
    }
    .hsw-gift-cta {
      align-self: stretch;
      text-align: center;
    }
  }
`
